import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, FileImage, Image } from "lucide-react";
import { Canvas as FabricCanvas, Text } from "fabric";
import FeedbackModal from "./FeedbackModal";
import DesignerLoginPopup from "./DesignerLoginPopup";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ExportDialogProps {
  canvasRef: React.RefObject<FabricCanvas | null>;
  trigger?: React.ReactNode;
}

const ExportDialog = ({ canvasRef, trigger }: ExportDialogProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  // Canvas ref is available for export operations

  // Check authentication on component mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
    } catch (error) {
      console.error("Error checking auth:", error);
      setIsAuthenticated(false);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginPopup(false);
    // Re-check authentication status
    checkAuth();
  };

  const handleAuthRequired = () => {
    setShowLoginPopup(true);
    toast.info("Please login to export your designs");
  };

  // Function to add watermark to canvas
  const addWatermark = (canvas: FabricCanvas) => {
    return new Promise<void>((resolve, reject) => {
      try {
        // Adding watermark to canvas

        // Calculate watermark position more reliably
        const canvasWidth = canvas.getWidth();
        const canvasHeight = canvas.getHeight();

        // Position watermark in bottom center
        const watermarkLeft = canvasWidth / 2; // Center horizontally
        const watermarkTop = canvasHeight - 35; // 35px from bottom edge

        // Position watermark in bottom center

        // Create watermark text with blue styling for better visibility
        const watermark = new Text("Made with Kente AI @kentemuseum.com", {
          left: watermarkLeft,
          top: watermarkTop,
          fontSize: 14, // Slightly smaller for longer text
          fontFamily: "Arial, sans-serif",
          fill: "#2563eb", // Blue color for better visibility
          fontWeight: "bold",
          selectable: false,
          evented: false,
          textAlign: "center", // Center the text
          originX: "center", // Center horizontally around the left position
          originY: "center", // Center vertically around the top position
          // Add a white background for contrast
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          padding: 6,
          // Add a subtle blue border
          stroke: "rgba(37, 99, 235, 0.5)",
          strokeWidth: 1,
        });

        // Store watermark reference for easy removal
        (watermark as any).isWatermark = true;

        // Add watermark to canvas
        canvas.add(watermark);
        canvas.bringObjectToFront(watermark); // Ensure it's on top
        canvas.renderAll();

        // Resolve after a delay to ensure rendering is complete
        setTimeout(() => {
          resolve();
        }, 200);
      } catch (error) {
        console.error("Error adding watermark:", error);
        reject(error);
      }
    });
  };

  const exportToPNG = async () => {
    // Check authentication first
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("Canvas ref is null");
      return;
    }

    // Exporting PNG

    try {
      // Try to add watermark, but continue if it fails
      try {
        await addWatermark(canvas);
      } catch (watermarkError) {
        console.warn("Watermark failed, proceeding without watermark");
      }

      const dataURL = canvas.toDataURL({
        format: "png",
        quality: 1,
        multiplier: 2, // Higher resolution
      });

      // PNG dataURL created successfully

      const link = document.createElement("a");
      link.download = `kente-design-${Date.now()}.png`;
      link.href = dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Download initiated

      // Track the download
      try {
        // Attempting to track PNG download

        const { data: trackData, error: trackError } = await supabase.rpc(
          "track_download" as any,
          {
            download_type_param: "png",
            design_name_param: `kente-design-${Date.now()}.png`,
            file_size_param: dataURL.length,
          }
        );

        if (trackError) {
          console.error("Failed to track PNG download:", trackError);
          toast.error(
            "Download completed but tracking failed: " + trackError.message
          );
        } else if (trackData && trackData.success) {
          // PNG download tracked successfully
          toast.success("Download tracked successfully!");
        } else {
          console.error("Unexpected tracking response:", trackData);
          toast.warning(
            "Download completed but tracking response was unexpected"
          );
        }
      } catch (trackErr) {
        console.error("Error tracking PNG download:", trackErr);
        toast.error("Download completed but tracking failed");
      }

      // Remove watermark after export with a small delay
      setTimeout(() => {
        const watermark = canvas
          .getObjects()
          .find((obj) => (obj as any).isWatermark === true);
        if (watermark) {
          canvas.remove(watermark);
          canvas.renderAll();
        }
      }, 500);

      toast.success("PNG exported successfully!");
    } catch (error) {
      console.error("PNG export failed:", error);
      toast.error("PNG export failed. Please try again.");
    }
  };

  const exportToJPEG = async () => {
    // Check authentication first
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("Canvas ref is null");
      return;
    }

    // Exporting JPEG

    try {
      // Try to add watermark, but continue if it fails
      try {
        await addWatermark(canvas);
      } catch (watermarkError) {
        console.warn("Watermark failed, proceeding without watermark");
      }

      const dataURL = canvas.toDataURL({
        format: "jpeg",
        quality: 0.9,
        multiplier: 2, // Higher resolution
      });

      // JPEG dataURL created successfully

      const link = document.createElement("a");
      link.download = `kente-design-${Date.now()}.jpg`;
      link.href = dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Download initiated

      // Track the download
      try {
        // Attempting to track JPEG download

        const { data: trackData, error: trackError } = await supabase.rpc(
          "track_download" as any,
          {
            download_type_param: "jpeg",
            design_name_param: `kente-design-${Date.now()}.jpg`,
            file_size_param: dataURL.length,
          }
        );

        if (trackError) {
          console.error("Failed to track JPEG download:", trackError);
          toast.error(
            "Download completed but tracking failed: " + trackError.message
          );
        } else if (trackData && trackData.success) {
          // JPEG download tracked successfully
          toast.success("Download tracked successfully!");
        } else {
          console.error("Unexpected tracking response:", trackData);
          toast.warning(
            "Download completed but tracking response was unexpected"
          );
        }
      } catch (trackErr) {
        console.error("Error tracking JPEG download:", trackErr);
        toast.error("Download completed but tracking failed");
      }

      // Remove watermark after export with a small delay
      setTimeout(() => {
        const watermark = canvas
          .getObjects()
          .find((obj) => (obj as any).isWatermark === true);
        if (watermark) {
          canvas.remove(watermark);
          canvas.renderAll();
        }
      }, 500);

      toast.success("JPEG exported successfully!");
    } catch (error) {
      console.error("JPEG export failed:", error);
      toast.error("JPEG export failed. Please try again.");
    }
  };

  const handleQuickExport = async (format: "png" | "jpeg") => {
    setIsExporting(true);

    try {
      switch (format) {
        case "png":
          await exportToPNG();
          break;
        case "jpeg":
          await exportToJPEG();
          break;
      }

      // Show feedback modal after successful export
      setTimeout(() => {
        setShowFeedbackModal(true);
      }, 1000);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleFeedbackSubmit = async (feedback: {
    rating: number;
    category: string;
    message: string;
  }) => {
    try {
      // Get current user (if logged in)
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Get the current design data from canvas
      const canvas = canvasRef.current;
      const designData = canvas ? canvas.toJSON() : null;

      // Save feedback to database
      const { error } = await supabase.from("user_feedbacks").insert({
        user_id: user?.id || null,
        user_email: user?.email || null,
        rating: feedback.rating,
        category: feedback.category,
        message: feedback.message || null,
        design_data: designData,
        page_context: "designer_export",
        status: "new",
      });

      if (error) throw error;

      console.log("Feedback saved successfully");
      toast.success("Thank you for your feedback! 🙏");
    } catch (error) {
      console.error("Failed to save feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {trigger || (
            <Button
              variant="outline"
              size="sm"
              className="px-4 py-2"
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </>
              )}
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem
            onClick={() => handleQuickExport("png")}
            disabled={isExporting}
          >
            <Image className="mr-2 h-4 w-4" />
            <div>
              <div className="font-medium">PNG</div>
              <div className="text-xs text-muted-foreground">
                High quality, transparent
              </div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleQuickExport("jpeg")}
            disabled={isExporting}
          >
            <FileImage className="mr-2 h-4 w-4" />
            <div>
              <div className="font-medium">JPEG</div>
              <div className="text-xs text-muted-foreground">
                Good quality, smaller size
              </div>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onFeedbackSubmit={handleFeedbackSubmit}
      />

      <DesignerLoginPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
        onSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default ExportDialog;
